import { Subject, throwError, EMPTY } from 'rxjs';
import { finalize, takeUntil, catchError } from 'rxjs/operators';

import { HttpErrorResponse } from '@angular/common/http';
import { Inject, Component, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { UntypedFormArray } from '@angular/forms';

import { SnCamera } from '@lib/api/entities';
import { SnCamerasAPI } from '@lib/api/services';
import { SN_DRAWER_DATA, SnDrawerRef } from '@lib/ui/drawer';

@Component({
  selector: 'sn-camera-form-drawer',
  templateUrl: './camera-form-drawer.component.html',
})
export class CameraFormDrawerComponent implements OnDestroy {
  public readonly form = new UntypedFormArray([]);

  public readonly isNew: boolean;
  public camera: SnCamera;
  public isLoading = false;
  public loadingForm: boolean = false;

  private readonly _destroy$ = new Subject<void>();

  constructor(
    @Inject(SN_DRAWER_DATA) public data: SnCamera,
    private readonly _changeDetectorRef: ChangeDetectorRef,
    private readonly _drawerRef: SnDrawerRef,
    private readonly _camerasAPI: SnCamerasAPI,
  ) {
    this.isNew = !(data && data.id);
    this.camera = data;
    if (!this.isNew) {
      this.loadingForm = true;
      this._camerasAPI
        .getCamera(data.id)
        .pipe(takeUntil(this._destroy$))
        .subscribe({
          next: (camera) => {
            this.camera = camera;
            this.loadingForm = false;
            this._changeDetectorRef.detectChanges();
          },
          error: () => {
            this.loadingForm = false;
            this._changeDetectorRef.detectChanges();
          },
        });
    }
  }

  public ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  public closeDialog(): void {
    this._drawerRef.close();
  }

  public onFormSent(formData: Object): void {
    this.isLoading = true;

    const updated$ = this.data.parentCamera
      ? this._camerasAPI.updateDescendant(this.data.parentCamera, this.data.id, formData)
      : this._camerasAPI.updateCamera(this.camera.id, formData);
    const stream$ = this.isNew ? this._camerasAPI.createCamera(formData) : updated$;
    stream$
      .pipe(
        catchError((errorResponse, source$) => {
          if (errorResponse instanceof HttpErrorResponse && errorResponse.status === 400) {
            this.form.setErrors(errorResponse.error);

            return EMPTY;
          }

          return throwError(errorResponse);
        }),
        takeUntil(this._destroy$),
        finalize(() => {
          this.isLoading = false;
          this._changeDetectorRef.markForCheck();
        }),
      )
      .subscribe((targetCamera: SnCamera) => {
        this._drawerRef.close(targetCamera);
      });
  }
}
